import React, { useMemo } from "react";
import { keyExists, defaultLocale } from "i18n";
import { mapValues, assert } from "react/utils/tsUtil";
import { useRenderString } from "features/i18n/useStrings";
import { useLocale } from "features/i18n/useLocale";

type I18nComponentProps = {
    i18nKey: LocalizationKey;
    substitutions: Record<string, string | boolean | number | JSX.Element>;
};

/** Combine two arrays, like [a1, a2], [b1, b2] -> [a1, b1, a2, b2] */
function interleave<T>(arr1: T[], arr2: T[]) {
    assert(
        arr1.length === arr2.length,
        "Expected arrays to interleave to have the same length"
    );
    const result: T[] = [];
    for (let i = 0; i < arr1.length; i++) {
        result.push(arr1[i]);
        result.push(arr2[i]);
    }
    return result;
}
/**
 * Renders a localized string into HTML, allowing React components to be interpolated
 *
 * @example
 * Takes a string like:
 * ```
 *  "Example {{#foo}}foo text{{/foo}} and {{#bar}}bar text{{/bar}}."
 * ```
 * And example usage:
 * ```js
 * <I18nComponent
 *  i18nKey={key}
 *  substitutions={ foo: Foo, bar: { Component: Bar, props: {prop: "bar"}}}
 * />
 * ```
 * Resulting in:
 * ```html
 * Example <span><Foo>foo text</Foo></span> and <span><Bar>bar text</Bar>></span>.
 * ```
 */
export const I18nComponent = ({
    i18nKey,
    substitutions,
}: I18nComponentProps) => {
    const renderString = useRenderString();
    let locale = useLocale();
    // fallback to english translation if the i18nKey is absent in the given locale.
    const isTranslationAvailable = keyExists(i18nKey, locale);
    if (!isTranslationAvailable) {
        locale = defaultLocale;
    }
    const children = useMemo(() => {
        const COMPONENT_PLACEHOLDER = "%!%PLACE%!%"; // Unique, but arbitrary string
        // The components to insert into the text
        const components: React.ReactNode[] = [];
        // Check the localizationKey exists
        assert(
            isTranslationAvailable || keyExists(i18nKey, locale),
            `This is not a valid localization key: ${i18nKey}`
        );
        // The localized text, with COMPONENT_PLACEHOLDER where components should go
        const textWithPlaceholders = renderString(
            i18nKey,
            mapValues(substitutions, (val, key) => {
                if (
                    typeof val === "string" ||
                    typeof val === "boolean" ||
                    typeof val === "number"
                )
                    return val;
                // This is part of the mustache API, returns a lambda that is provided
                //  the text inside the mustache block.
                // For example with `{{#myComponent}}foo bar{{/myComponent}}`,
                //  `text` will be "foo bar"
                // Note: currently doesn't support interpolations inside blocks
                //  though mustache docs suggest it's possible.
                // See: https://mustache.github.io/mustache.5.html#Sections
                return () => (text: string) => {
                    components.push(
                        React.cloneElement(
                            val,
                            { key },
                            !text ? val.props.children : [text]
                        )
                    );
                    return COMPONENT_PLACEHOLDER;
                };
            })
        );
        const textParts: React.ReactNode[] = textWithPlaceholders.split(
            COMPONENT_PLACEHOLDER
        );
        const [firstTextPart, ...restOfTextParts] = textParts;
        return [firstTextPart].concat(interleave(components, restOfTextParts));
    }, [substitutions, i18nKey, locale]);

    return <React.Fragment children={children} />;
};
